import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  input,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AgilityButtonComponent } from '@agility-components/agility-button/agility-button.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { filter, forkJoin } from 'rxjs';
import {
  GlobalLoanSearchGQL,
  LoggedInUserRecentlyViewedLoansGQL,
} from 'src/app/graphql/generated';
import {
  GlobalLoanCardComponent,
  GlobalLoanCardLoan,
} from '../global-loan-card/global-loan-card.component';

@Component({
  selector: 'app-global-loan-search',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    AgilityButtonComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    GlobalLoanCardComponent,
  ],
  templateUrl: './global-loan-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalLoanSearchComponent implements OnInit {
  readonly recentlyViewedLoanSize = input(4);
  readonly closePopover = output<boolean>();

  readonly searchId = model('');

  readonly loans_ = signal<GlobalLoanCardLoan[]>([]);
  readonly searchedLoans_ = signal<GlobalLoanCardLoan[]>([]);
  readonly showNotFoundMessage_ = signal(false);
  readonly loadingSlotCount_ = signal(0);

  readonly loans = computed(() => this.loans_());
  readonly searchedLoans = computed(() => this.searchedLoans_());
  readonly showNotFoundMessage = computed(() => this.showNotFoundMessage_());
  readonly loadingSlotCount = computed(() => this.loadingSlotCount_());
  readonly loadingSlots = computed(() =>
    Array.from({ length: this.loadingSlotCount_() }, (_, index) => index)
  );

  constructor(
    private readonly globalLoanSearchGQL: GlobalLoanSearchGQL,
    private readonly loggedInUserRecentlyViewedLoansGQL: LoggedInUserRecentlyViewedLoansGQL,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.closePopover.emit(true));

    this.loadingSlotCount_.set(4);
    this.loggedInUserRecentlyViewedLoansGQL
      .fetch({ variables: { size: this.recentlyViewedLoanSize() } })
      .subscribe(({ data }) => {
        const recent = data?.loggedInUser?.recentlyViewedLoans;
        if (recent?.length) {
          this.loans_.set(recent);
        }
        this.loadingSlotCount_.set(0);
      });
  }

  onEnteringLoanNumber(): void {
    const query = this.searchId().trim();
    if (!query) {
      return;
    }

    this.showNotFoundMessage_.set(false);
    this.loadingSlotCount_.set(5);
    this.loans_.set([]);
    this.searchedLoans_.set([]);

    forkJoin({
      search: this.globalLoanSearchGQL.fetch({
        variables: {
          filter: {
            searchString: query,
            size: this.recentlyViewedLoanSize(),
          },
        },
      }),
      recent: this.loggedInUserRecentlyViewedLoansGQL.fetch({ variables: {} }),
    }).subscribe(({ search, recent }) => {
      const matchedLoans = (search.data?.loans ?? []).filter(
        (loan) => loan.displayLoanId
      );
      const recentlyViewedLoans =
        recent.data?.loggedInUser?.recentlyViewedLoans ?? [];

      if (matchedLoans.length) {
        this.loans_.set(recentlyViewedLoans);
        this.searchedLoans_.set(matchedLoans);
      } else {
        this.showNotFoundMessage_.set(true);
      }
      this.loadingSlotCount_.set(0);
    });
  }
}
